/**
 * useChat Hook — WebSocket management for real-time messaging
 *
 * Adapted from bachata-nation-next useChat.ts.
 * - Uses Vidnight JWT auth (via useAuthStore)
 * - Auto-reconnection with exponential backoff
 * - Heartbeat every 5 seconds
 * - Message buffering during reconnection
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuthStore } from '@/features/auth';
import { API_URL } from '@/network/http';
import type {
  WsIncomingEvent,
  WsOutgoingEvent,
  WsMessageEvent,
  WsTypingEvent,
  WsReadEvent,
  WsReactionEvent,
  WsDeleteEvent,
  WsEditEvent,
  WsPresenceEvent,
  WsPollEvent,
  WsPollVoteEvent,
  WsStarEvent,
  WsPinEvent,
  WsThreadUpdateEvent,
  Mention,
} from '../types';

// Convert http(s):// to ws(s)://
function getWsUrl(conversationId: string, token: string): string {
  const base = API_URL.replace(/^http/, 'ws');
  return `${base}/messaging/conversations/${conversationId}/ws?token=${encodeURIComponent(token)}`;
}

interface UseChatOptions {
  conversationId: string | null;
  enabled?: boolean;
  onMessage?: (event: WsMessageEvent) => void;
  onTyping?: (event: WsTypingEvent) => void;
  onRead?: (event: WsReadEvent) => void;
  onReaction?: (event: WsReactionEvent) => void;
  onDelete?: (event: WsDeleteEvent) => void;
  onEdit?: (event: WsEditEvent) => void;
  onPresence?: (event: WsPresenceEvent) => void;
  onPoll?: (event: WsPollEvent) => void;
  onPollVote?: (event: WsPollVoteEvent) => void;
  onStar?: (event: WsStarEvent) => void;
  onPin?: (event: WsPinEvent) => void;
  onThreadUpdate?: (event: WsThreadUpdateEvent) => void;
  onError?: (code: string, message: string) => void;
  onAck?: (originalType: string, sequenceId?: number, messageId?: string) => void;
}

interface UseChatReturn {
  isConnected: boolean;
  isReconnecting: boolean;
  send: (event: WsOutgoingEvent) => void;
  sendText: (content: string, replyToId?: string, mentions?: Mention[]) => void;
  sendMedia: (mediaType: 'image' | 'video' | 'audio' | 'file', mediaUrl: string, mediaThumbnail?: string, mediaMetadata?: { width?: number; height?: number; duration?: number; mimeType?: string; fileName?: string; fileSize?: number; waveformPeaks?: number[] }, content?: string) => void;
  sendTyping: (isTyping: boolean) => void;
  sendRead: (upToSequenceId: number) => void;
  sendReaction: (messageId: string, emoji: string, action: 'add' | 'remove') => void;
  sendDelete: (messageId: string) => void;
  sendEdit: (messageId: string, content: string) => void;
  sendPoll: (question: string, options: string[]) => void;
  sendPollVote: (messageId: string, optionIndex: number) => void;
  sendStar: (messageId: string, action: 'add' | 'remove') => void;
  sendPin: (messageId: string, action: 'pin' | 'unpin') => void;
  disconnect: () => void;
}

const RECONNECT_DELAYS = [1000, 2000, 4000, 8000, 16000, 30000];
const HEARTBEAT_INTERVAL = 5000;

export function useChat(options: UseChatOptions): UseChatReturn {
  const {
    conversationId,
    enabled = true,
    onMessage,
    onTyping,
    onRead,
    onReaction,
    onDelete,
    onEdit,
    onPresence,
    onPoll,
    onPollVote,
    onStar,
    onPin,
    onThreadUpdate,
    onError,
    onAck,
  } = options;

  const [isConnected, setIsConnected] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const heartbeatTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const messageBufferRef = useRef<WsOutgoingEvent[]>([]);
  const intentionalCloseRef = useRef(false);

  // Callbacks stored in refs to avoid dependency issues
  const callbacksRef = useRef({
    onMessage, onTyping, onRead, onReaction, onDelete, onEdit,
    onPresence, onPoll, onPollVote, onStar, onPin, onThreadUpdate, onError, onAck,
  });
  callbacksRef.current = {
    onMessage, onTyping, onRead, onReaction, onDelete, onEdit,
    onPresence, onPoll, onPollVote, onStar, onPin, onThreadUpdate, onError, onAck,
  };

  const startHeartbeat = useCallback(() => {
    if (heartbeatTimerRef.current) clearInterval(heartbeatTimerRef.current);
    heartbeatTimerRef.current = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'heartbeat' }));
      }
    }, HEARTBEAT_INTERVAL);
  }, []);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
  }, []);

  const flushBuffer = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN && messageBufferRef.current.length > 0) {
      const buffer = [...messageBufferRef.current];
      messageBufferRef.current = [];
      for (const msg of buffer) {
        wsRef.current.send(JSON.stringify(msg));
      }
    }
  }, []);

  // Store connect in a ref so onclose always calls the latest version
  const connectRef = useRef<() => void>(() => {});

  const connect = useCallback(() => {
    if (!conversationId || !enabled) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    // Always read the LATEST token from the store (not the closure)
    // to avoid stale-token reconnects after the HTTP interceptor refreshes
    const currentToken = useAuthStore.getState().accessToken;
    if (!currentToken) return;

    const url = getWsUrl(conversationId, currentToken);
    const ws = new WebSocket(url);
    wsRef.current = ws;
    intentionalCloseRef.current = false;

    ws.onopen = () => {
      setIsConnected(true);
      setIsReconnecting(false);
      reconnectAttemptRef.current = 0;
      startHeartbeat();
      flushBuffer();
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data) as WsIncomingEvent;
        const cbs = callbacksRef.current;

        switch (data.type) {
          case 'message':
            cbs.onMessage?.(data);
            break;
          case 'typing':
            cbs.onTyping?.(data);
            break;
          case 'read':
            cbs.onRead?.(data);
            break;
          case 'reaction':
            cbs.onReaction?.(data);
            break;
          case 'delete':
            cbs.onDelete?.(data);
            break;
          case 'edit':
            cbs.onEdit?.(data);
            break;
          case 'presence':
            cbs.onPresence?.(data);
            break;
          case 'poll':
            cbs.onPoll?.(data);
            break;
          case 'poll_vote':
            cbs.onPollVote?.(data);
            break;
          case 'star':
            cbs.onStar?.(data);
            break;
          case 'pin':
            cbs.onPin?.(data);
            break;
          case 'thread_update':
            cbs.onThreadUpdate?.(data);
            break;
          case 'error':
            cbs.onError?.(data.code, data.message);
            break;
          case 'ack':
            cbs.onAck?.(data.originalType, data.sequenceId, data.messageId);
            break;
          case 'heartbeat_ack':
            // Heartbeat acknowledged — no action needed
            break;
          default:
            // Handle non-typed server events (e.g. persist_warning)
            if ((data as { type?: string; messageId?: string }).type === 'persist_warning') {
              console.warn('[useChat] Message persistence delayed:', (data as { messageId?: string }).messageId);
            }
            break;
        }
      } catch (err) {
        console.error('[useChat] Failed to parse message:', err);
      }
    };

    ws.onclose = () => {
      setIsConnected(false);
      stopHeartbeat();

      if (!intentionalCloseRef.current) {
        const attempt = reconnectAttemptRef.current;
        const delay = RECONNECT_DELAYS[Math.min(attempt, RECONNECT_DELAYS.length - 1)];
        reconnectAttemptRef.current++;
        setIsReconnecting(true);

        reconnectTimerRef.current = setTimeout(() => {
          // Use ref to always call the latest connect function (fresh token)
          connectRef.current();
        }, delay);
      }
    };

    ws.onerror = () => {
      if (!intentionalCloseRef.current) {
        console.error('[useChat] WebSocket error');
      }
    };
  }, [conversationId, enabled, startHeartbeat, stopHeartbeat, flushBuffer]);

  // Keep connectRef in sync
  connectRef.current = connect;

  const disconnect = useCallback(() => {
    intentionalCloseRef.current = true;
    stopHeartbeat();

    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setIsConnected(false);
    setIsReconnecting(false);
  }, [stopHeartbeat]);

  // Connect on mount, disconnect on unmount.
  useEffect(() => {
    connect();
    return () => disconnect();
  }, [connect, disconnect]);

  // Send function
  const send = useCallback((event: WsOutgoingEvent) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(event));
    } else {
      messageBufferRef.current.push(event);
    }
  }, []);

  // Convenience methods
  const sendText = useCallback((content: string, replyToId?: string, mentions?: Mention[]) => {
    send({ type: 'text', content, replyToId, mentions });
  }, [send]);

  const sendTyping = useCallback((isTyping: boolean) => {
    send({ type: 'typing', isTyping });
  }, [send]);

  const sendRead = useCallback((upToSequenceId: number) => {
    send({ type: 'read', upToSequenceId });
  }, [send]);

  const sendReaction = useCallback((messageId: string, emoji: string, action: 'add' | 'remove') => {
    send({ type: 'reaction', messageId, emoji, action });
  }, [send]);

  const sendDelete = useCallback((messageId: string) => {
    send({ type: 'delete', messageId });
  }, [send]);

  const sendEdit = useCallback((messageId: string, content: string) => {
    send({ type: 'edit', messageId, content });
  }, [send]);

  const sendPoll = useCallback((question: string, options: string[]) => {
    send({ type: 'poll', question, options });
  }, [send]);

  const sendPollVote = useCallback((messageId: string, optionIndex: number) => {
    send({ type: 'poll_vote', messageId, optionIndex });
  }, [send]);

  const sendMedia = useCallback((
    mediaType: 'image' | 'video' | 'audio' | 'file',
    mediaUrl: string,
    mediaThumbnail?: string,
    mediaMetadata?: { width?: number; height?: number; duration?: number; mimeType?: string; fileName?: string; fileSize?: number; waveformPeaks?: number[] },
    content?: string,
  ) => {
    send({ type: mediaType, mediaUrl, mediaThumbnail, mediaMetadata, content });
  }, [send]);

  const sendStar = useCallback((messageId: string, action: 'add' | 'remove') => {
    send({ type: 'star', messageId, action });
  }, [send]);

  const sendPin = useCallback((messageId: string, action: 'pin' | 'unpin') => {
    send({ type: 'pin', messageId, action });
  }, [send]);

  return {
    isConnected,
    isReconnecting,
    send,
    sendText,
    sendMedia,
    sendTyping,
    sendRead,
    sendReaction,
    sendDelete,
    sendEdit,
    sendPoll,
    sendPollVote,
    sendStar,
    sendPin,
    disconnect,
  };
}

export default useChat;
